/*
 * The content column.
 *
 * There is no narrow variant and no wide one any more: every page runs the full
 * window, and the side padding is the shared gutter so the navigation, the
 * content and the footer all line up on the same two edges. `.pageWide` and
 * `.shellWide` used to be the difference between a 72rem reading page and a
 * 100rem grid page, and with the measure at 100% they had nothing left to say.
 */
.page {
  width: 100%;
  max-width: var(--gb-measure);
  margin: 0 auto;
  padding: var(--gb-space-6) var(--gb-gutter) var(--gb-space-16);
  /*
   * Load-bearing, and its absence is what put a horizontal scrollbar under
   * every page in the app.
   *
   * There is no `box-sizing` reset anywhere in this system — the tokens set the
   * document's type and ground and stop there — so this box was content-box,
   * and `width: 100%` under content-box means *the content is as wide as the
   * shell* and the gutter is added outside it. The measure is the whole window,
   * so that made `<main>` two gutters wider than the window it sits in: up to
   * 8rem of overflow, the page scrolling sideways, and the title and its rule
   * running off the right edge with nothing under them to explain why.
   *
   * `max-width: var(--gb-measure)` was already saying "never wider than the
   * measure". Under content-box that was not true of the box anybody can see,
   * only of the content inside it. This makes the declaration mean what it
   * reads as.
   */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-8);
}

/*
 * The navigation gets its own full-width band, so a bleed section below it
 * starts hard against the header rather than after a gap.
 *
 * Sticky, because a storefront is a long scroll and the way back to Browse
 * should not be a trip to the top. Opaque rather than translucent: capsule art
 * scrolling visibly under a blurred bar is the single most dated effect
 * available, and the brand has no gradients or blurs anywhere else.
 */
/*
 * The header, as a bar rather than as a region.
 *
 * Section 07's application mockups draw it one way: a solid field, tight, mono,
 * uppercase, wordmark left and links right. This was the opposite — the page's
 * own `--gb-surface` with a hairline under it and two rems of vertical padding,
 * so there was no bar, only a tall empty band that happened to contain some
 * small text.
 *
 * `--gb-surface-raised` rather than Ink. Every space is dark, so an Ink bar on
 * the store's warm near-black would be a difference nobody could see; the
 * raised neutral is each panel's own "this is an object" ground and reads as a
 * distinct field on all three.
 *
 * The accent takes the bottom edge as a flat rule — "colour as flat fields and
 * rules, blocks not gradients". Two pixels, because a hairline in the accent
 * reads as an artefact and a thick band reads as a second navigation.
 */
.navBar {
  border-bottom: 2px solid var(--gb-accent);
  position: sticky;
  top: 0;
  z-index: 20;
  background: var(--gb-surface-raised);
}

/*
 * The bar's own measure and gutter.
 *
 * This was `.navBar .page` — the bar borrowed the page's container and then
 * unwound the three properties of it that did not apply. That worked in the
 * React app and did nothing at all on the storefront, which renders `SiteNav`
 * directly and never had a `.navBar` or a `.page` around it: no bar, no
 * background, no accent rule, and no horizontal gutter, so the wordmark and the
 * links sat flush against the window.
 *
 * `SiteNav` renders both wrappers itself now, so the two applications cannot
 * disagree about whether the header has a bar. That is why this is its own
 * class rather than a page override: it needs a measure and a gutter and none
 * of the column layout `.page` also brings.
 */
.navBarInner {
  width: 100%;
  max-width: var(--gb-measure);
  margin: 0 auto;
  padding: 0 var(--gb-gutter);
  box-sizing: border-box;
}

/*
 * Two rows, and the split is by kind rather than by fit.
 *
 * **This was one row and everything in it was the same weight** — a 1.125rem
 * wordmark, 0.6875rem mono destinations and a 0.6875rem `Sign in`, spread
 * across the measure with `space-between`. A header's first job is to answer
 * *where can I go*, and this one answered it in eleven-pixel type next to two
 * things that are not answers to it at all: the mark is not a destination, and
 * installing the launcher is not a page.
 *
 * So identity and the one non-navigational action go above, small, and every
 * place a person can actually go goes below at several times the size. The two
 * rows are a hierarchy rather than a wrap, which is why they are a column here
 * and not `flex-wrap` doing it by accident on a narrow window.
 *
 * `--gb-nav-major` is the second row's size and is declared here so that the
 * links, the current-page marker and the register button all take it from one
 * place — three rules that have to agree, and a header where they disagree is
 * a button that does not sit on the line beside it. It clamps rather than
 * fixes: 2.25rem is three times the `--gb-ui-label` the first row's button is
 * set in, and a phone cannot hold `Downloads` at that size.
 */
.nav {
  --gb-nav-major: clamp(1.25rem, 3vw, 2.25rem);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
  /*
   * Vertical only — the gutter is `.navBarInner`'s, so that the accent rule and
   * the bar's ground run the full width of the window while the contents stay
   * on the page's measure.
   */
  padding: var(--gb-space-4) 0;
}

/*
 * The identity row.
 *
 * `center`, not `baseline`. Baseline aligns a 1.125rem display wordmark with a
 * 0.75rem button label by their feet, which drops the button low and leaves the
 * row looking like two things that failed to line up. They are optically
 * different sizes and want a shared centre line.
 */
.navTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
}

/*
 * The wayfinding row: destinations on the left, the way in on the right.
 *
 * A hairline above it, so the two rows read as a decision somebody made rather
 * than as one row that ran out of width. It is `--gb-rule` and not the accent —
 * the accent in this bar is the bottom edge and the register button, and a
 * third instance of it would stop meaning anything.
 */
.navMain {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-6);
  flex-wrap: wrap;
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  padding-top: var(--gb-space-3);
}

/*
 * The furniture, at the far end of the identity row.
 *
 * `Launcher` and `Admin` were destinations in the row below until the slots
 * arrived — entries of two different kinds, all at `--gb-nav-major`, so a member
 * of staff read `ADMIN` as one of the largest words in the header on every page
 * of the shop. Neither is a place the product *is*: see `NavItem.slot` in
 * `@goodbuild/rules/site`, which is where that is argued.
 *
 * One group rather than four loose children of `.navTop`, so the row stays two
 * objects — the mark, and the things you reach for. `space-between` on the
 * parent then does the whole layout, and the installer keeps its place at the
 * end of the row it has always sat at the end of.
 *
 * `center` and not `baseline`: a mono label and an outline button have
 * different box heights and no meaningful shared baseline, and aligning them by
 * their feet drops the button low — the same mistake `.navTop` documents.
 *
 * `--gb-space-4` and not the destinations' `--gb-space-6`. These are set at
 * label size, and the wide gutter that keeps 36-pixel words apart leaves
 * 12-pixel ones adrift from each other with a button floating past them.
 */
.navUtility {
  display: flex;
  align-items: center;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
  min-width: 0;
}

/*
 * The signed-out bar: one row, because there is nothing to rank.
 *
 * A visitor gets one destination — the catalogue — so the stacked bar drew a
 * second row holding a single word, under a hairline whose only job was to
 * separate the wordmark from the link beneath it. That is a hierarchy drawn
 * between a thing and nothing, and it is the first thing anybody arriving here
 * sees.
 *
 * It is `.nav` with the column undone rather than a bar of its own, so the
 * padding, the measure and every rule that reaches `.navPrimary` and
 * `.navAccount` are the ones the signed-in bar uses. **Which means source order
 * is load-bearing:** this is one class where `.nav` is one class, so it has to
 * come after it in the file to win. It does, and it must stay that way.
 *
 * **`--gb-nav-major` comes down to about the wordmark's size, and that is the
 * point rather than a detail.** The two-row bar sets it to three times the
 * label size because the second row *is* the wayfinding — there is nothing
 * beside it to compete with. On one line the mark is beside it, and `GAMES` at
 * 2.25rem next to a 1.125rem wordmark is exactly the failure the two-row bar
 * was built to fix, made in the other direction: a headline with the product's
 * name as its footnote. One line, one optical weight.
 *
 * The row gap is the smaller of the two, because a wrap here is a phone running
 * out of width rather than a decision — it should read as one row that folded,
 * not as the two rows this shape exists to avoid.
 */
.navSingle {
  --gb-nav-major: clamp(0.9375rem, 1.6vw, 1.25rem);
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--gb-space-3) var(--gb-space-6);
}

/*
 * The two ends of that row: what this is and where you can go, then what you
 * can do about it.
 *
 * Grouped rather than five children under one `space-between`, which would
 * spread the mark, the link and the three controls at even intervals across the
 * measure — five unrelated objects rather than two clusters with a decision on
 * the right-hand one.
 *
 * **`center` on both, and the left one is the trap.** A wordmark and a mono
 * destination are both type, so a baseline looks like the obvious answer — but
 * `Logo` renders `.logoRow`, an `inline-flex` whose first child is the Progress
 * Strip, and a flex item whose own first item has no text baseline gets one
 * synthesised from its bottom margin edge instead. So `baseline` here would
 * align the *bars* with the bottom of `GAMES` and hang the wordmark off it by
 * whatever the strip happens to be tall. `.navTop` has said `center` since it
 * was written, for the neighbouring reason its note gives.
 */
.navSingleStart {
  display: flex;
  align-items: center;
  gap: var(--gb-space-6);
  flex-wrap: wrap;
  min-width: 0;
}

.navSingleEnd {
  display: flex;
  align-items: center;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
  min-width: 0;
}

.navLinks {
  display: flex;
  gap: var(--gb-space-6);
  flex-wrap: wrap;
}

/*
 * `.wordmark` used to live here and was used by nothing.
 *
 * The mark in the bar is drawn by `Logo`, which brings its own type from
 * `brand.module.css` and takes its size as a prop — so this was a second,
 * unreachable definition of the wordmark's cut, which is exactly the kind of
 * thing section 02 means when it says the mark has one definition.
 */

.rule {
  border: 0;
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  margin: 0;
}

/* A measurement and the thing it measures. The number never shrinks. */
.stat {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-1);
}

.statValue {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-weight: 700;
  line-height: 1;
  font-size: 1.5rem;
  color: var(--gb-text);
}

.statLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-muted);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: var(--gb-space-6);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: var(--gb-space-4);
  text-decoration: none;
  background: var(--gb-surface-raised);
}

.card:hover {
  border-color: var(--gb-accent);
}

.cardTitle {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 125,
    'wght' 800;
  text-transform: uppercase;
  font-size: 1.125rem;
  line-height: 1.05;
  margin: 0;
}

.cardMeta {
  color: var(--gb-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.statRow {
  display: flex;
  gap: var(--gb-space-8);
  flex-wrap: wrap;
}

/*
 * A row of things to press.
 *
 * `.statRow` was standing in for this, which is a two-rem gap meant to hold
 * measurements apart and reads as two unrelated buttons when it holds a primary
 * and its secondary. They are one decision with two answers and should sit
 * close enough to be read as such.
 *
 * `center`, so a button and a plain link in the same row share a centre line
 * rather than a baseline — the button has padding the link does not.
 */
.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gb-space-4);
}

.button {
  font-family: var(--gb-font-text);
  font-size: var(--gb-ui-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--gb-accent);
  color: var(--gb-on-accent);
  border: 0;
  border-radius: var(--gb-radius);
  padding: var(--gb-space-3) var(--gb-space-6);
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

/*
 * Disabled takes the panel's faint type colour, not a fixed grey.
 *
 * `--gb-ink-40` is #8c8c99, which read as "greyed out" on Paper and reads as
 * "a lighter, more prominent button" on Ink — the state inverted its own
 * meaning when the spaces went dark.
 */
.button:disabled {
  background: var(--gb-text-faint);
  cursor: not-allowed;
}

/*
 * The outline button. Fills with its own border colour on hover and inverts its
 * text, the same wipe as `.ctaGhost` in `brand.module.css` — see the note there
 * for why it is a pseudo-element and why it wipes rather than fades.
 *
 * Every state is guarded on `:not(:disabled)`. This button is the submit on the
 * portal and on the admin decision forms, where the disabled state means a
 * request is in flight; a control that still animates under the pointer while
 * it is refusing input reads as a broken button rather than a busy one.
 */
.buttonSecondary {
  composes: button;
  background: transparent;
  color: var(--gb-text);
  border: var(--gb-rule-width) solid var(--gb-text);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  transition: color var(--gb-motion) var(--gb-ease);
}

.buttonSecondary::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--gb-text);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--gb-motion) var(--gb-ease-out);
}

.buttonSecondary:hover:not(:disabled),
.buttonSecondary:focus-visible:not(:disabled) {
  color: var(--gb-surface);
}

.buttonSecondary:hover:not(:disabled)::before,
.buttonSecondary:focus-visible:not(:disabled)::before {
  transform: scaleX(1);
}

/*
 * Getting the launcher, offered from the quiet row.
 *
 * The library's own outline button rather than a header-shaped copy of one, and
 * `composes` rather than a second set of declarations for two reasons. A
 * border-only button in this system fills with its border colour on hover, from
 * the left, as a pseudo-element — `brand.test.ts` pins that rule and says a
 * third implementation must not arrive without it, and the way to not arrive
 * with one is to reuse the second. And the press animation in `tokens.css` is
 * keyed on `a[class*='button']`, so a control named anything else is the one
 * thing on the page that does not acknowledge being pressed.
 *
 * Outline and not the fill: the accent in this header belongs to `Register`,
 * and two filled buttons one row apart are two primary actions competing for
 * the same click — which is what section 04 reserves the accent to prevent.
 *
 * Outside `.navPrimary` and `.navAccount`, so none of the link rules below
 * reach it. It is a control, and it should not grow the sweeping accent
 * underline the destinations have.
 *
 * **Here, and not up in the bar's own section, because `composes` is not a
 * cascade.** postcss resolves the name against what it has already read, so a
 * rule that composes a class defined further down the file is a hard build
 * error rather than a class that quietly loses: *referenced class name
 * "buttonSecondary" in composes not found*. `.navRegister` composes `.button`
 * and is below it for the same reason.
 */
.navLauncher {
  composes: buttonSecondary;
  /* Two words that must stay one line: the row beside it is the wordmark. */
  white-space: nowrap;
}

/*
 * A banner stating that there is not enough player data yet (section 10.7).
 * A rule in the panel's accent rather than a warning colour, because this is a
 * state that will pass rather than something wrong.
 */
.provisional {
  border-left: 3px solid var(--gb-accent);
  padding: var(--gb-space-3) var(--gb-space-4);
  background: var(--gb-surface-sunken);
}

.empty {
  border: var(--gb-rule-width) dashed var(--gb-rule);
  padding: var(--gb-space-8);
  text-align: center;
  color: var(--gb-text-muted);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
}

.table th {
  text-align: left;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-muted);
  padding: var(--gb-space-2) var(--gb-space-3) var(--gb-space-2) 0;
  border-bottom: var(--gb-rule-width) solid var(--gb-rule);
}

.table td {
  padding: var(--gb-space-3) var(--gb-space-3) var(--gb-space-3) 0;
  /* The panel's rule, so a table has rules on Ink as well as on Paper. */
  border-bottom: var(--gb-rule-width) solid var(--gb-rule);
  vertical-align: top;
}

/*
 * A state, said in one word.
 *
 * The portal used to print the column out of the database — `in_review`,
 * `coming_soon`, with the underscores swapped for spaces — which asks a studio
 * to read our schema to find out what is happening to their game. A tag is the
 * same fact wearing the label voice the rest of this product's furniture uses.
 *
 * A rule around it and no fill. A filled chip would be a second object on the
 * row and would need a colour per state, which is four or five colours on a
 * panel that has one. What varies is the rule, and only between two values:
 * the panel's own rule for a state that is simply true, and the accent for one
 * that is waiting on the person reading it.
 */
.tag {
  display: inline-block;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-muted);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: var(--gb-space-1) var(--gb-space-2);
  white-space: nowrap;
}

/*
 * The one state that is waiting on the reader.
 *
 * A draft nobody has sent, a title that came back declined. Section 07 gives
 * the accent to one thing per view; on this table that thing is the row the
 * studio has to do something about, which is the only reason to look at the
 * page at all.
 */
.tagAction {
  composes: tag;
  border-color: var(--gb-accent);
  color: var(--gb-text);
}

.numeric {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

.sectionTitle {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 125,
    'wght' 800;
  text-transform: uppercase;
  font-size: 1.25rem;
  margin: 0;
}

/*
 * The line at the top of a screen: what this page is, and one fact about it.
 *
 * `Screen` and the buy page have both asked for this class since they were
 * written and it was defined nowhere, so `ui.sectionHead` was `undefined` and
 * both rendered `<div class="">`. That left the headline and its aside as two
 * block elements stacking — the aside on its own line under the title, in the
 * body face, reading as a stray sentence rather than as the title's annotation,
 * and making the top of every one of these pages a block taller than it needs
 * to be.
 *
 * `baseline`, not `center`: the aside is a caption on the headline and should
 * sit on its line, and `flex-wrap` is what keeps that from turning into a
 * squeezed two-column layout on a phone.
 */
.sectionHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gb-space-2) var(--gb-space-6);
}

/*
 * The aside, in the voice everything that names rather than says uses.
 *
 * Same treatment as `.statLabel`, `.fieldLabel` and the nav: mono, uppercase,
 * tracked, muted. Styled by position rather than by a second class, because the
 * only thing that goes beside the headline is that one fact.
 */
.sectionHead > span {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-variant-numeric: tabular-nums;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
}

/* --- Site chrome -------------------------------------------------------- */

/*
 * The page becomes a column so the footer can be pushed to the bottom on short
 * pages. Without this, a page with one paragraph leaves the footer floating in
 * the middle of the viewport, which reads as a rendering bug.
 */
/*
 * `--gb-measure` is not set here any more; it comes from the token layer and is
 * the whole window. It used to be 72rem on this shell and 100rem on `.shellWide`
 * — see the note beside the token for why both are gone.
 */
.shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.shell.shellBackdropped {
  background: transparent;
}

.shellMain {
  flex: 1;
}

/*
 * The content column inside the page.
 *
 * `.page` used to be `<main>` itself and carried this column with it. It is a
 * wrapper now, because a work surface puts a navigation rail beside the
 * content and a rail inside `<main>` is announced as part of the document's
 * main content. The gap moved here with the column so that spacing between a
 * page's sections is unchanged on every screen that never grew a rail.
 */
.pageMain {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-8);
  min-width: 0;
}

/* The primary nav sits apart from account actions so a signed-out visitor is
   not scanning past Library and Orders to find what the platform is. */
.navPrimary {
  display: flex;
  gap: var(--gb-space-6);
  flex-wrap: wrap;
  align-items: baseline;
  min-width: 0;
}

.navAccount {
  display: flex;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
}

/*
 * Navigation type.
 *
 * **The links were body text**, at body size, in Archivo — which is why the
 * header read as a generic site menu on a product whose entire visual language
 * is mono data-labels. Everything else that names something rather than saying
 * something — `.statLabel`, `.fieldLabel`, the footer's column heads, the
 * waitlist's whole chapter rail — is mono, uppercase and tracked. The nav is
 * the most-seen instance of that and was the one that opted out.
 *
 * Applied to the two link groups rather than to `.nav`, so the wordmark keeps
 * its display cut and the account cluster keeps whatever the application puts
 * in it.
 */
.navPrimary a,
.navAccount a:not(.navRegister) {
  font-family: var(--gb-font-mono);
  /*
   * `--gb-nav-major`, declared on `.nav` — a little over three times the data
   * label this row used to be set in. The size is the whole point of the second
   * row: these are the answers to "where can I go", and they should be the
   * loudest thing in the header rather than a footnote beside the wordmark.
   *
   * The tracking comes down as the size goes up, and it has to. 0.12em is what
   * makes eleven-pixel mono readable and what makes 36-pixel mono a word with
   * gaps in it — the same letterspacing does opposite things at the two ends of
   * a scale, which is why this is a number here rather than the token.
   */
  font-size: var(--gb-nav-major);
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
  line-height: 1.1;
  transition: color var(--gb-motion) var(--gb-ease-out);
}

.navPrimary a:hover,
.navPrimary a:focus-visible,
.navAccount a:not(.navRegister):hover,
.navAccount a:not(.navRegister):focus-visible {
  color: var(--gb-text);
}

/*
 * The furniture's own voice: the same mono label the rest of the header's small
 * type is set in, and deliberately not the destinations' size.
 *
 * `--gb-ui-label` is what `.statLabel`, `.fieldLabel` and the footer's column
 * heads are set in, and it is what the whole bar used to be — the row above is
 * the exception rather than this. The tracking goes back up to 0.12em with it,
 * for the reason the rule above gives from the other end: the same
 * letterspacing does opposite things at the two ends of a scale, and eleven
 * pixels of mono is unreadable without it.
 *
 * `:not(.navLauncher)` because the installer sits in this group and is a
 * control, not a destination. It composes `buttonSecondary` and brings its own
 * type, its own box and the press in `tokens.css`; reaching it here would set
 * its face and tracking from underneath and leave a button that does not match
 * any other button in the product.
 */
.navUtility a:not(.navLauncher) {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-ui-label);
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
  line-height: 1.1;
  /* Two of the three are one word; `Launcher` must not fold beside the mark. */
  white-space: nowrap;
  transition: color var(--gb-motion) var(--gb-ease-out);
}

.navUtility a:not(.navLauncher):hover,
.navUtility a:not(.navLauncher):focus-visible {
  color: var(--gb-text);
}

/*
 * The other door, and the only filled thing in the header.
 *
 * **`Sign in` had no counterpart at all** — a shop whose header offered the way
 * back in and never the way in. Set as a fifth link beside it, `Register` would
 * be a word a first-time visitor has to stop and tell apart from `Sign in`, and
 * it is the one of the two that most people arriving here need. So it is a
 * control rather than a link: the accent as a flat field, its own weight, and
 * the header's single instance of the colour that means *this one* (section
 * 04).
 *
 * `.button` rather than a header-shaped copy of it, so the fill, the ground it
 * inverts its type to and the press in `tokens.css` are all the ones the rest
 * of the product uses. What is overridden is the scale and the voice: this
 * button sits in a row of mono destinations and has to be their size, not the
 * form-submit size `.button` is written at.
 *
 * **No hover fill.** The filled button in this system does not have one —
 * `.ctaPrimary` in `brand.module.css` says why, and the wipe belongs to the
 * outline buttons that have somewhere to wipe *to*. The press is the feedback,
 * and every button in the product gives the same one.
 *
 * The padding is `em`, so the box grows and shrinks with the row's clamp rather
 * than being a slab with a small word in it on a phone.
 */
.navRegister {
  composes: button;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-nav-major);
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.1;
  padding: 0.3em 0.75em;
  white-space: nowrap;
}

/*
 * The page you are on: the accent, and the entry has not moved.
 *
 * **It was a square tick and a bold weight, and both of them moved the word.**
 * `display: inline-flex` with a `gap` pushes the label right by the width of
 * the marker plus half a rem, and `font-weight: 700` widens every glyph in it —
 * so clicking `Library` shifted `Library` sideways and shoved everything after
 * it along the row. At the second row's size that is not a
 * subtle drift; it is the whole bar rearranging itself under the pointer that
 * just clicked it. A navigation that moves when you use it is a navigation you
 * have to re-read.
 *
 * So the state is paint and nothing else. The metrics are identical to every
 * other entry — same face, same weight, same box — and `aria-current="page"`
 * carries it for anybody who is not looking at the colour, which is what
 * `ChromeLink` has passed since #113 and is why the square is not load-bearing.
 *
 * The accent rather than `--gb-text`. `--gb-text` is a step of the neutral ramp
 * and reads as *emphasised*; the entry you are on is a state, and state is what
 * the accent is for (section 04) — here as the ground the label is knocked out
 * of, which is why this is `--gb-on-accent` and not the accent itself.
 *
 * The colour is declared here as well as animated below, because the animation
 * is where it actually comes from — see `gb-nav-fill`. This is the resting
 * value, for the case where the keyframes never run.
 *
 * **Qualified by the group, and it has to be.** A bare `.navCurrent` is one
 * class where `.navPrimary a` is a class and a type, so the group's muted colour
 * outranks it and the entry you are on renders exactly like the ones you are
 * not. Only in `.navPrimary`, because the bar marks a destination and nothing in
 * the account cluster is one.
 */
.navPrimary a.navCurrent {
  color: var(--gb-on-accent);
}

/*
 * Hover cannot take it back.
 *
 * `.navPrimary a:hover` sets `--gb-text`, so pointing at the page you are
 * already on greyed it down to look like every other entry. The running
 * animation below wins over both of these — a filled animation beats any normal
 * declaration — so this pair matters only when the keyframes are gone, which is
 * the same case the rule above exists for.
 */
.navPrimary a.navCurrent:hover,
.navPrimary a.navCurrent:focus-visible {
  color: var(--gb-on-accent);
}

/*
 * And the same marker up in the furniture row, because some of the entries
 * that carry it live there now.
 *
 * **This is the rule that would have been forgotten.** `Admin` was marked as a
 * destination; moving it to a row whose selector did not mention
 * `.navCurrent` would have taken the marker off them silently — a member of
 * staff standing in the console with nothing in the bar saying so, and no
 * failure anywhere to notice it. The class is still on the element and
 * `aria-current="page"` is still spoken; it is only the colour that needs a
 * second home.
 *
 * Qualified by the group for the reason the rule above gives: a bare
 * `.navCurrent` is one class where `.navUtility a:not(…)` is a class, a type
 * and a negation, so the group's muted colour would outrank it and the entry
 * you are standing on would render exactly like the ones you are not.
 *
 * The fill in the motion section is shared and needs nothing here — it is
 * keyed on `.navCurrent` itself, and its height is `em`, so it is the label's
 * own height at this size as much as at the row below's.
 */
.navUtility a.navCurrent:not(.navLauncher) {
  color: var(--gb-on-accent);
}

.navUtility a.navCurrent:not(.navLauncher):hover,
.navUtility a.navCurrent:not(.navLauncher):focus-visible {
  color: var(--gb-on-accent);
}

/* --- The work surfaces' side navigation --------------------------------- */

/*
 * Two columns on a work surface, one on the store.
 *
 * The rail is a fixed column rather than a fraction, because it holds one word
 * per line and a percentage would make it grow on a wide monitor into a band of
 * whitespace with `Builds` floating in it. 14rem fits the longest label this
 * navigation has with room for the marker.
 *
 * `minmax(0, 1fr)` on the content column and not `1fr`, which is `minmax(auto,
 * 1fr)`: a grid track sized `auto` refuses to go narrower than its widest
 * unbreakable child, so one wide table would push the whole page sideways
 * instead of scrolling inside itself. This is the same failure `.page` fixed
 * with `box-sizing`, arriving through the other door.
 */
.workBody {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--gb-space-8);
  align-items: start;
}

@media (min-width: 60rem) {
  .workBody {
    grid-template-columns: 14rem minmax(0, 1fr);
    gap: var(--gb-space-12);
  }
}

/*
 * The rail itself.
 *
 * Sticky under the header, so on a long screen — a build list, a table of
 * titles — the way to the next screen does not require scrolling back up. The
 * offset is the header's height plus a gutter; the header is sticky at `top: 0`
 * and this has to clear it rather than slide under it.
 *
 * Nothing about it is a box: no panel, no border around it, no fill. Section 02
 * is flat fields and rules, and a rail in a rounded card is the single clearest
 * tell that a page was assembled from a component library rather than designed.
 * What separates it from the content is a rule, on the side the content is on.
 */
.workNav {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-6);
}

@media (min-width: 60rem) {
  .workNav {
    position: sticky;
    top: calc(var(--gb-space-16) + var(--gb-space-2));
    padding-right: var(--gb-space-6);
    border-right: var(--gb-rule-width) solid var(--gb-rule);
  }
}

.workNavSection {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

/*
 * The group label. The data-label voice, which is what the rest of this
 * product's furniture is set in.
 *
 * Faint rather than muted: it is the one thing on the rail nobody clicks, and
 * it should not read as a disabled link above the ones that are.
 */
.workNavTitle {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-faint);
  margin: 0;
}

.workNavList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/*
 * An entry.
 *
 * The accent arrives as a rule down the left edge rather than as a fill or a
 * colour on the type: a filled row is a box, and coloured labels would put the
 * panel's signal colour on five things at once — section 07 gives the accent to
 * one thing per view and this rail is not it.
 *
 * The rule is always there and is transparent until it is wanted, so nothing
 * moves between states. A `border-left` appearing on hover shifts the label
 * three pixels, which is the fidgeting every navigation built this way does.
 */
.workNavLink {
  display: block;
  padding: var(--gb-space-2) var(--gb-space-3);
  border-left: 2px solid transparent;
  color: var(--gb-text-muted);
  text-decoration: none;
  font-size: var(--gb-body);
  line-height: 1.3;
  transition:
    color var(--gb-motion-fast) var(--gb-ease),
    border-color var(--gb-motion-fast) var(--gb-ease);
}

.workNavLink:hover,
.workNavLink:focus-visible {
  color: var(--gb-text);
  border-left-color: var(--gb-rule);
}

/*
 * The page you are on.
 *
 * The accent, at full weight, and it is the only entry that gets either. The
 * top bar answers the same question with a tick to the left of the label; a
 * vertical list answers it better with the edge, because the edge is already
 * the shape of the list.
 */
.workNavCurrent,
.workNavCurrent:hover {
  color: var(--gb-text);
  font-weight: 700;
  border-left-color: var(--gb-accent);
}

/*
 * Narrow screens: the rail becomes a row above the content.
 *
 * A horizontal scroller rather than a wrapped block, so a phone spends one line
 * on navigation instead of six. The section labels go with it — they earn their
 * place in a column and are noise in a strip.
 */
@media (max-width: 59.999rem) {
  .workNav {
    flex-direction: row;
    gap: var(--gb-space-4);
    overflow-x: auto;
    padding-bottom: var(--gb-space-2);
    border-bottom: var(--gb-rule-width) solid var(--gb-rule);
  }

  .workNavSection {
    flex-direction: row;
    gap: var(--gb-space-1);
  }

  .workNavTitle {
    display: none;
  }

  .workNavList {
    flex-direction: row;
  }

  .workNavLink {
    white-space: nowrap;
    border-left: 0;
    border-bottom: 2px solid transparent;
  }

  .workNavLink:hover,
  .workNavLink:focus-visible {
    border-bottom-color: var(--gb-rule);
  }

  .workNavCurrent,
  .workNavCurrent:hover {
    border-bottom-color: var(--gb-accent);
  }
}

.footer {
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  margin-top: var(--gb-space-16);
  /* The same gutter as the page, so the two share an edge. */
  padding: var(--gb-space-12) var(--gb-gutter) var(--gb-space-8);
}

.footerInner {
  max-width: var(--gb-measure);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-12);
}

.footerColumns {
  display: grid;
  /* auto-fit rather than a fixed count so a column can be added or removed
     without touching the breakpoint. */
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: var(--gb-space-8);
}

.footerColumn {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
}

.footerColumn ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

.footerColumn a {
  font-size: var(--gb-body);
  color: var(--gb-text-muted);
  text-decoration: none;
}

.footerColumn a:hover {
  color: var(--gb-text);
  text-decoration: underline;
}

.footerBase {
  display: flex;
  justify-content: space-between;
  gap: var(--gb-space-6);
  flex-wrap: wrap;
  align-items: baseline;
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  padding-top: var(--gb-space-6);
}

/*
 * `.footerLegal` used to be here and is gone with the row it drew.
 *
 * It was a flex row of `Terms`, `Privacy` and `Refunds` in the colophon, at
 * `--gb-ui-label` in the muted ramp — which is the copyright line's treatment,
 * and it is how the row read: furniture. The documents are a named footer
 * column now, set like every other footer link, because `Refunds` in particular
 * is a page somebody comes down here specifically to find. See `LEGAL_NAV`.
 *
 * The rules below were shared between the two rows and are the social row's
 * alone now. They keep the label size deliberately: a social handle is a
 * colophon entry — it names an account rather than a place in this product —
 * and it is the one thing left down here that should stay quiet.
 */
.footerSocials a {
  color: var(--gb-text-muted);
  font-size: var(--gb-ui-label);
  text-decoration: none;
}

.footerSocials a:hover {
  color: var(--gb-text);
  text-decoration: underline;
}

.footerSocials {
  display: flex;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
}

/* --- Editorial pages ---------------------------------------------------- */

/* Prose measure, from the token rather than from a count written here. See
   `--gb-prose` in tokens.css for why a fixed one left every editorial page as a
   strip down the left of a wide screen. */
.prose {
  max-width: var(--gb-prose);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
}

.lede {
  font-size: var(--gb-subhead);
  line-height: 1.4;
  color: var(--gb-text);
  max-width: var(--gb-prose);
}

.pointGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  gap: var(--gb-space-8);
}

.point {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  border-top: 2px solid var(--gb-text);
  padding-top: var(--gb-space-3);
}

.pointBody {
  color: var(--gb-text-muted);
  margin: 0;
}

/* --- A column of figures that add up ------------------------------------ */

/*
 * The earnings simulation on the submission flow, and anything else that has to
 * show where a number came from rather than only what it is.
 *
 * A grid rather than a table: there is one column of labels and one of figures,
 * no header row, and nothing here is tabular data in the sense a screen reader
 * would want to navigate cell by cell. A definition list is what this is.
 *
 * `tabular-nums` on the figures, from `.numeric`, so the decimal points line up
 * down the column — which is the entire reason to set money in a column at all.
 */
.breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
  border-top: 2px solid var(--gb-text);
  padding-top: var(--gb-space-3);
}

.breakdownRows {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  margin: 0;
}

.breakdownRow {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--gb-space-4);
  align-items: baseline;
}

/* A subtotal: ruled off from what fed into it, and no longer muted. */
.breakdownRow[data-total='true'] {
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  padding-top: var(--gb-space-2);
}

.breakdownLabel {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  color: var(--gb-text-muted);
}

.breakdownRow[data-total='true'] .breakdownLabel {
  color: var(--gb-text);
}

/* Where the figure beside it came from. Smaller than the label it sits under,
   because it is an aside rather than a second label. */
.breakdownNote {
  font-size: var(--gb-data-label);
  color: var(--gb-text-faint);
}

.breakdownValue {
  margin: 0;
  color: var(--gb-text-muted);
}

.breakdownRow[data-total='true'] .breakdownValue {
  color: var(--gb-text);
}

.breakdownCaption {
  margin: 0;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-faint);
}

/* An unconfigured channel says so rather than rendering an empty box. */
.pending {
  color: var(--gb-text-faint);
  font-style: italic;
}

/* --- Motion ------------------------------------------------------------- */

/*
 * Navigation links get the same left-to-right underline as prose links, drawn
 * as a rule under the item rather than a text decoration so it lines up across
 * a row of different word lengths.
 */
.navPrimary a,
.navAccount a:not(.navRegister),
.navUtility a:not(.navLauncher) {
  position: relative;
  text-decoration: none;
  padding-bottom: 2px;
}

.navPrimary a::after,
.navAccount a:not(.navRegister)::after,
.navUtility a:not(.navLauncher)::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: var(--gb-accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--gb-motion) var(--gb-ease-out);
}

.navPrimary a:hover::after,
.navAccount a:not(.navRegister):hover::after,
.navUtility a:not(.navLauncher):hover::after,
.navPrimary a:focus-visible::after,
.navAccount a:not(.navRegister):focus-visible::after,
.navUtility a:not(.navLauncher):focus-visible::after {
  transform: scaleX(1);
}

.navCurrent {
  --gb-nav-fill-height: calc(1.15em + 2px);
  background-image: linear-gradient(var(--gb-accent), var(--gb-accent));
  background-repeat: no-repeat;
  background-position: left bottom;
  background-size: 100% var(--gb-nav-fill-height);
  animation: gb-nav-fill var(--gb-motion-slow) var(--gb-ease) both;
}

@keyframes gb-nav-fill {
  from {
    background-size: 100% 2px;
    color: var(--gb-text-muted);
  }

  25% {
    color: var(--gb-text-muted);
  }

  80% {
    color: var(--gb-on-accent);
  }

  to {
    background-size: 100% var(--gb-nav-fill-height);
    color: var(--gb-on-accent);
  }
}

/* Cards lift a little and take the accent rule. Two pixels, once. */
.card {
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    transform var(--gb-motion) var(--gb-ease-out);
}

.card:hover {
  transform: translateY(-2px);
}

/* A table row is a target on the portal and the admin queue. */
.table tbody tr {
  transition: background var(--gb-motion-fast) var(--gb-ease);
}

.table tbody tr:hover {
  background: var(--gb-surface-sunken);
}

/* The empty state's dashed rule warms up on hover, so it reads as an invitation
   rather than a fence. Section 8: an empty state is an invitation to act. */
.empty {
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    color var(--gb-motion) var(--gb-ease);
}

.empty:hover {
  border-color: var(--gb-accent);
  color: var(--gb-text);
}

/* --- Arrival ------------------------------------------------------------ */

/*
 * The page deals itself in.
 *
 * Every top-level block of a page arrives in sequence rather than the whole
 * screen appearing at once, which is the difference between a document whose
 * contents were swapped and a place somebody moved to. It is the other half of
 * the scene change in `motion.module.css`: the blade crosses the window and the
 * page is dealt out behind it.
 *
 * The same rise and fade `Reveal` uses, at the same distance and on the same
 * curve, so a page's own arrival and the sections it reveals further down are
 * one gesture rather than two effects. Nothing here scales, bounces or blurs.
 *
 * **`:where()`, so this loses every argument it gets into.** `:where()`
 * contributes no specificity at all, which makes these the weakest rules in the
 * system — and that is the point: `.channelBar` in `brand.module.css` already
 * animates itself, as a wipe, once per page. Two rules of equal weight setting
 * `animation` would be decided by whichever stylesheet the bundler happened to
 * emit second, which is not a thing anybody should have to know. At zero
 * specificity, anything with an arrival of its own keeps it.
 *
 * It replays on navigation because `SiteChrome` keys `<main>` on the path — a
 * CSS animation runs when an element is inserted, and React resolves many route
 * swaps by editing children in place. See the note there.
 *
 * **`backwards`, and a `forwards` fill here would silently disable `Reveal` on
 * every page.** An animation in its fill period beats a normal declaration in
 * the cascade, so a forwards fill would hold each block at `opacity: 1` for the
 * rest of the visit — including the blocks `Reveal` hides at `opacity: 0` while
 * it waits for them to be scrolled to. Nothing would look broken. Every section
 * below the fold would simply be there already, and the reveal would be a
 * component that runs an observer and changes nothing.
 *
 * `backwards` fills only *before* the animation starts, which is the half this
 * needs: it is what holds a block invisible through its stagger delay. After it
 * ends the element goes back to whatever the stylesheet says, which for an
 * ordinary section is visible and for an unrevealed one is hidden.
 */
:where(.pageMain) > * {
  animation: gb-deal var(--gb-motion-slow) var(--gb-ease-out) backwards;
}

/*
 * The stagger, written out, and capped at six.
 *
 * Multiples of `--gb-stagger` rather than a hand-written millisecond ladder, so
 * a page's blocks and a `RevealGroup`'s children are spaced by the same number.
 * Past the sixth everything arrives together: nobody watches the ninth section
 * of a page wait its turn, and a long page would otherwise spend a second and a
 * half assembling itself.
 */
:where(.pageMain > *:nth-child(2)) {
  animation-delay: var(--gb-stagger);
}
:where(.pageMain > *:nth-child(3)) {
  animation-delay: calc(var(--gb-stagger) * 2);
}
:where(.pageMain > *:nth-child(4)) {
  animation-delay: calc(var(--gb-stagger) * 3);
}
:where(.pageMain > *:nth-child(5)) {
  animation-delay: calc(var(--gb-stagger) * 4);
}
:where(.pageMain > *:nth-child(6)) {
  animation-delay: calc(var(--gb-stagger) * 5);
}
:where(.pageMain > *:nth-child(n + 7)) {
  animation-delay: calc(var(--gb-stagger) * 6);
}

@keyframes gb-deal {
  from {
    opacity: 0;
    transform: translateY(0.875rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* --- The scroll rule ---------------------------------------------------- */

/*
 * How far down the page you are, drawn as the bar's own bottom edge.
 *
 * The header already ends in a two-pixel accent rule. This makes that rule mean
 * something: it fills left to right as the document scrolls, so every page
 * carries a read-out of where in it you are without spending a pixel of layout
 * on one. It is the closest thing here to a heads-up display, and it is the
 * cheapest — no element in the flow, no listener, no measurement in script.
 *
 * **It is real data, which is the only reason it is allowed.** Hard rule 14 and
 * section 06 are about figures displaying values they never had; scroll offset
 * over scroll length is measured, exactly, by the engine, every frame. It is
 * also deliberately not a Progress Strip: that device is bars, it means review
 * score by version, and section 03 reserves it. This is one continuous rule and
 * cannot be read as five.
 *
 * `animation-timeline` is the whole mechanism and there is no script fallback,
 * because the failure is free — see below.
 */
.navProgress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 2px;
  background: var(--gb-accent);
  transform-origin: left;
  /*
   * The resting state is the full rule, and that is what makes this safe to
   * ship everywhere.
   *
   * A browser with no scroll timelines never runs the animation and draws this,
   * which is the accent rule the bar has always had. A browser that has them
   * but is showing a page too short to scroll gets an *inactive* timeline —
   * whose animation has no resolved time and so applies no effect — and draws
   * this too. Neither case needs detecting, and neither leaves an empty track
   * where an accent rule used to be.
   */
  transform: scaleX(1);
}

@supports (animation-timeline: scroll()) {
  /*
   * The bar's own edge becomes the track.
   *
   * A wash of the panel's accent over the bar's own ground rather than a
   * neutral: the header's accent rule is part of what the header *is*, and a
   * page at the top of its scroll should not look like a page whose identity
   * rule failed to load. At a bit over a quarter it is unmistakably the same
   * colour and unmistakably not the fill, which is the whole job.
   *
   * A tint of the panel's own accent, which is what the palette rules allow —
   * this is not a fifth colour any more than muted type is. Mixed with the
   * bar's ground rather than with `transparent`, so it composites once here
   * instead of over whatever happens to be scrolling underneath.
   *
   * Only inside the `@supports`. Without a fill to contrast with, this would
   * simply be the header's accent rule quietly dimmed for no reason.
   */
  .navBar {
    border-bottom-color: color-mix(in srgb, var(--gb-accent) 28%, var(--gb-surface-raised));
  }

  /*
   * Longhands, not the shorthand, and it is load-bearing. `animation: name
   * linear` sets `animation-duration` to `0s`; a scroll-driven animation needs
   * it left at `auto`, which is what makes the timeline's own range the
   * duration. The shorthand form of this rule runs for no time at all and
   * paints nothing.
   */
  .navProgress {
    animation-name: gb-scroll-progress;
    animation-timing-function: linear;
    animation-fill-mode: both;
    animation-timeline: scroll(root block);
  }
}

@keyframes gb-scroll-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .card:hover {
    transform: none;
  }

  .navPrimary a::after,
  .navAccount a:not(.navRegister)::after,
  .navUtility a:not(.navLauncher)::after {
    transition: none;
  }

  /*
   * The page arrives finished.
   *
   * `animation: none` rather than letting the global reset shorten it: the
   * reset collapses an animation to a single 0.01ms iteration, which for these
   * lands on the end state and is harmless — but only by luck, and the same
   * reset is what would leave a `both`-filled effect parked wherever it stopped
   * if the keyframes were ever written the other way round.
   */
  :where(.pageMain) > * {
    animation: none;
  }

  /*
   * The scroll rule stops reading and goes back to being a rule.
   *
   * A scroll-driven animation is not motion in the sense this query is about —
   * nothing moves unless the reader moves it — but the global reset would give
   * it a 0.01ms duration and take it away from the timeline, which parks it at
   * one end or the other. A full accent rule is the honest resting state and is
   * what the bar looks like everywhere else.
   */
  .navBar {
    border-bottom-color: var(--gb-accent);
  }

  .navProgress {
    animation-name: none;
    transform: scaleX(1);
  }
}

/* --- Forms ------------------------------------------------------------- */

/*
 * There were no form styles in this system at all, and it showed: the sign-in
 * page was a bare `<label>Email <input></label>` on the default ground, which
 * is the first thing anybody with an account ever sees.
 *
 * The register is the waitlist's, because that page is the one piece of this
 * product whose surface was finished: hard edges, a rule around everything, a
 * raised ground for anything that is a *thing* rather than a background, mono
 * at label size with the data tracking for anything naming a value, and the
 * accent reserved for state rather than spent on decoration.
 */

/*
 * A box that is an object rather than a region.
 *
 * The one shape the waitlist used for every panel, every sheet and every card.
 * `--gb-radius` is zero and stays zero: section 02's whole geometry is square,
 * and a rounded corner here would be the one soft edge on the site.
 */
.panel {
  background: var(--gb-surface-raised);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: var(--gb-space-8);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

/*
 * What a field is called.
 *
 * Mono, uppercase, tracked, faint — the same treatment `.statLabel` gives the
 * caption under a figure, because they are the same thing: a name for a value.
 * Using body type here is what made the old form read as a browser default.
 */
.fieldLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

/*
 * The control itself.
 *
 * Sunken rather than raised, so a field reads as a well cut into the panel
 * around it rather than as another object sitting on it — which is also what
 * makes a panel of six fields read as one form.
 *
 * `font: inherit` is load-bearing: a bare input takes the platform's own font
 * stack and size, and on Windows that is the single loudest thing on a page
 * otherwise set in Archivo.
 */
.input {
  font: inherit;
  font-size: var(--gb-body);
  color: var(--gb-text);
  background: var(--gb-surface-sunken);
  border: var(--gb-rule-width) solid var(--gb-rule);
  border-radius: var(--gb-radius);
  padding: var(--gb-space-3);
  width: 100%;
  box-sizing: border-box;
  transition: border-color var(--gb-motion) var(--gb-ease-out);
}

.input::placeholder {
  color: var(--gb-text-faint);
}

/*
 * The accent, on the field being filled in.
 *
 * `:focus-visible` alone would leave a mouse user with no indication of which
 * field they clicked into, so this is plain `:focus` for the border and the
 * global focus ring stays for keyboard traversal on top of it.
 */
.input:focus {
  outline: none;
  border-color: var(--gb-accent);
}

.input:focus-visible {
  outline: var(--gb-focus-width) solid var(--gb-accent);
  outline-offset: var(--gb-focus-offset);
}

textarea.input {
  resize: vertical;
  line-height: 1.5;
  min-height: 8rem;
}

/*
 * Something the reader has to act on: an error, a warning, a result.
 *
 * A rule down the left in the accent rather than a coloured field. The palette
 * has one accent per surface and no red, and inventing one for errors would put
 * a colour on the page that section 04 does not own — so weight and position
 * carry the emphasis instead.
 */
.notice {
  border-left: 3px solid var(--gb-accent);
  background: var(--gb-surface-sunken);
  padding: var(--gb-space-3) var(--gb-space-4);
  margin: 0;
}

/* --- Signing in --------------------------------------------------------- */

/*
 * The one page on this origin a signed-out person ever sees.
 *
 * It bypassed `Page` entirely, so it had no ground, no chrome and no wordmark —
 * a form floating on the default background with nothing saying whose it was.
 * This gives it the ground the rest of the surface has and centres one panel in
 * it, which is the whole layout.
 */
.authShell {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gb-space-8) var(--gb-gutter);
  background: var(--gb-surface);
}

.authPanel {
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-6);
}

/* The mark, over the form, with room under it. Somebody arriving from an email
   link needs to know whose sign-in this is before they type a password. */
.authHead {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  align-items: flex-start;
}

.authFoot {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  padding-top: var(--gb-space-4);
  border-top: var(--gb-rule-width) solid var(--gb-rule);
}

.authFoot p {
  margin: 0;
  font-size: var(--gb-ui-label);
  color: var(--gb-text-muted);
}

/* --- Ticking a box ------------------------------------------------------ */

/*
 * A thing somebody is agreeing to.
 *
 * **The whole row is the control**, not just the box: a `<label>` with the
 * input inside it, so the sentence is as clickable as the eleven pixels beside
 * it. That is not a nicety on a touch screen, where an unpadded checkbox is
 * below every platform's minimum target and the miss lands on the page behind.
 *
 * Bordered like a card rather than left bare, for the same reason `.provider`
 * is: it is a control, and a control that looks like a paragraph is one people
 * read past. It answers to `:focus-within` as well as to `:hover`, because the
 * box takes the focus and the ring belongs on the whole row it is part of.
 *
 * `align-items: flex-start` because the sentence wraps to two lines on a phone
 * and a centred box then floats halfway down the side of it.
 */
.check {
  display: flex;
  align-items: flex-start;
  gap: var(--gb-space-3);
  padding: var(--gb-space-3) var(--gb-space-4);
  border: var(--gb-rule-width) solid var(--gb-rule);
  border-radius: var(--gb-radius);
  color: var(--gb-text);
  cursor: pointer;
  transition:
    border-color var(--gb-motion) var(--gb-ease-out),
    color var(--gb-motion) var(--gb-ease-out);
}

.check:hover,
.check:focus-within {
  border-color: var(--gb-accent);
}

/* Ticked reads as ticked from across the room, not only at the box. */
.check[data-checked='true'] {
  border-color: var(--gb-accent);
}

/*
 * The box itself, native.
 *
 * A `<select>` had to be rebuilt in this system's own vocabulary because the
 * operating system draws its open list and cannot be told not to. A checkbox
 * has no such surface: `accent-color` takes it, so one line puts it in the
 * panel's colour on every platform and it keeps the keyboard behaviour, the
 * screen-reader announcement and the indeterminate state that a `<div
 * role="checkbox">` would have to reimplement and would reimplement worse.
 */
.checkBox {
  flex: none;
  width: 1.05rem;
  height: 1.05rem;
  /* Aligns the box with the cap height of the first line rather than its box. */
  margin-top: 0.15rem;
  accent-color: var(--gb-accent);
  cursor: pointer;
}

/*
 * The sentence being agreed to.
 *
 * **No link in it, deliberately**, which is why there is no rule for one here.
 * The whole row is the label, so a click anywhere inside it toggles the box —
 * including on a link, which would tick the agreement on its way out of the
 * page. The documents are above it instead, in full.
 */
.checkText {
  font-size: var(--gb-ui-label);
  line-height: 1.5;
}

/* --- Social sign-in ----------------------------------------------------- */

/*
 * The other ways in.
 *
 * Outlined rather than filled, and deliberately quieter than the submit button
 * beside them: the accent is this panel's one loud colour and spending it on
 * three buttons at once would leave nothing saying which is the primary action.
 * Discord's blurple and Twitch's purple are not used either — section 04 is
 * explicit that four channels is the system and a fifth breaks wayfinding, and
 * a provider's own brand colour on our surface is exactly that fifth.
 */
.providers {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

.provider {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 700;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gb-space-3);
  padding: var(--gb-space-3) var(--gb-space-4);
  background: none;
  color: var(--gb-text);
  border: var(--gb-rule-width) solid var(--gb-rule);
  border-radius: var(--gb-radius);
  cursor: pointer;
  transition:
    border-color var(--gb-motion) var(--gb-ease-out),
    color var(--gb-motion) var(--gb-ease-out);
}

.provider:hover:not(:disabled),
.provider:focus-visible:not(:disabled) {
  border-color: var(--gb-accent);
  color: var(--gb-accent);
}

.provider:disabled {
  color: var(--gb-text-faint);
  cursor: progress;
}

.provider svg {
  width: 1rem;
  height: 1rem;
  flex: none;
  fill: currentColor;
}

/*
 * A document to go and read, in a tab of its own.
 *
 * `.provider`'s box exactly, composed rather than copied: the two sit on the
 * same kind of screen doing the same kind of job — an outlined row you press to
 * leave this form for a moment — and a second set of numbers for that would
 * drift the first time either was touched. What it adds is what an anchor needs
 * and a button does not: the underline off, and room between the label and the
 * mark that says it opens elsewhere.
 *
 * **Below `.provider` rather than up beside the tick boxes it appears with, and
 * that is not tidiness.** `composes` is resolved by postcss-modules-scope in
 * source order: a rule that composes a class declared further down the file
 * fails the build outright with *referenced class name "provider" in composes
 * not found*, which reads like the class is missing rather than early. So a
 * composing rule goes after the rule it composes — the same reason
 * `.selectTrigger` sits below `.input`.
 */
.docLink {
  composes: provider;
  text-decoration: none;
  justify-content: space-between;
}

/*
 * "or", with a rule either side.
 *
 * A separator rather than a heading: the email form above it and the providers
 * below it are two ways to do one thing, and a heading would rank them.
 */
.orRule {
  display: flex;
  align-items: center;
  gap: var(--gb-space-3);
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.orRule::before,
.orRule::after {
  content: '';
  flex: 1;
  height: var(--gb-rule-width);
  background: var(--gb-rule);
}

/* --- Dropdowns ---------------------------------------------------------- */

/*
 * The custom dropdown, from `select.tsx`.
 *
 * A native `<select>` gets its closed box styled and its open list drawn by the
 * operating system, in the platform's own font on the platform's own ground —
 * which on a product whose every surface is dark means a white menu opening out
 * of a sunken well. That is the whole reason the list below is a `<ul>` in the
 * page.
 *
 * `position: relative` is what the list is positioned against, and
 * `display: inline-flex` is what stops the wrapper eating a whole row: the
 * price form puts one of these beside two inputs and a button.
 */
.select {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  /*
   * A floor rather than a width, and it is doing real work.
   *
   * The wrapper shrinks to its trigger and the trigger holds whichever option
   * is chosen, so without this the control would change width every time
   * somebody picked a different one — a row of admin decisions reflowing under
   * the pointer. Ten rems clears every label in the product; a caller that
   * wants narrower lowers the floor rather than setting a width, which
   * `min-width` would win against anyway.
   */
  min-width: 10rem;
}

/*
 * The closed control, which is `.input`'s box exactly.
 *
 * Composed rather than copied, so a dropdown standing next to a text field in
 * the same row cannot drift from it — same ground, same rule, same padding,
 * same focus behaviour. What it adds is the room for the mark and the alignment
 * a button does not get for free.
 */
.selectTrigger {
  composes: input;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-3);
  text-align: left;
  cursor: pointer;
}

.selectTrigger:disabled {
  color: var(--gb-text-faint);
  cursor: not-allowed;
}

/* The chosen value reads as a value: body type, like the text a field holds. */
.selectValue {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Nothing chosen yet takes the same faint as `.input::placeholder`, so an empty
   dropdown and an empty field are empty in the same voice. */
.selectPlaceholder {
  composes: selectValue;
  color: var(--gb-text-faint);
}

/*
 * The mark, and the one thing on this control that moves.
 *
 * It turns rather than swapping for a second glyph: a chevron that flips is a
 * state change you can see out of the corner of your eye, and there is only one
 * shape to keep aligned.
 */
.selectMark {
  width: 0.625rem;
  height: 0.375rem;
  flex: none;
  color: var(--gb-text-faint);
  transition: transform var(--gb-motion-fast) var(--gb-ease-out);
}

.selectTrigger[aria-expanded='true'] .selectMark {
  transform: rotate(180deg);
  color: var(--gb-accent);
}

/*
 * The list.
 *
 * Raised rather than sunken: the well is the closed control, and the list is an
 * object sitting on top of the page. No shadow and no radius — section 02's
 * geometry is square and this system has no elevation layer, so a rule and an
 * opaque ground are what separate it from whatever is underneath.
 *
 * Opaque is load-bearing rather than decorative. This overlays a table of
 * decisions on the admin screens, and anything showing through it would be text
 * on text.
 */
.selectList {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 30;
  margin: var(--gb-space-1) 0 0;
  padding: 0;
  list-style: none;
  max-height: 15rem;
  overflow-y: auto;
  background: var(--gb-surface-raised);
  border: var(--gb-rule-width) solid var(--gb-accent);
  border-radius: var(--gb-radius);
}

/*
 * A row in the list.
 *
 * The highlight is the accent as a flat field, which is the same move
 * `.button` makes — colour as a block, never a tint. `data-active` rather than
 * `:hover` because the keyboard and the pointer have to light the same row:
 * `select.tsx` sets it from both, so arrowing down and moving the mouse cannot
 * produce two highlights at once.
 */
.selectOption {
  padding: var(--gb-space-2) var(--gb-space-3);
  font-size: var(--gb-body);
  color: var(--gb-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-3);
}

.selectOption[data-active] {
  background: var(--gb-accent);
  color: var(--gb-on-accent);
}

.selectOption[aria-disabled='true'] {
  color: var(--gb-text-faint);
  cursor: not-allowed;
}

/*
 * The chosen one, marked in the list.
 *
 * A tick would be a second glyph to keep; the mono label voice this system
 * already uses for "what a value is" does the job, and it survives the row
 * being highlighted because it is set in `currentColor`.
 */
.selectOption[aria-selected='true']::after {
  content: '●';
  font-size: 0.5rem;
  line-height: 1;
  flex: none;
}

/*
 * A real `<select>`, wearing the same box.
 *
 * For the one dropdown that cannot be the component above: the storefront's
 * publishing form is pinned to work with no JavaScript, and the page that asks
 * studios to take us seriously is the last place to need a browser. So the box
 * is ours and the list stays the platform's.
 *
 * `appearance: none` removes the platform's own control furniture, and the
 * chevron is drawn as a background image because there is no element to put one
 * in. `background-color` has to be restated alongside it: the shorthand would
 * drop the ground and leave the well transparent.
 */
.selectNative {
  composes: input;
  appearance: none;
  cursor: pointer;
  padding-right: var(--gb-space-8);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%236e6e80' stroke-width='1.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--gb-space-3) center;
  background-size: 0.625rem 0.375rem;
}

/*
 * The one thing a native list can be told, and it is worth telling it.
 *
 * Firefox and Chrome on Windows do honour these on an `<option>`; macOS ignores
 * them entirely. Setting them costs nothing and means the list is on the right
 * ground on the platform most of this product's studios are on.
 */
.selectNative option {
  background: var(--gb-surface-raised);
  color: var(--gb-text);
}

@media (prefers-reduced-motion: reduce) {
  .selectMark {
    transition: none;
  }
}

/* --- Working through a queue -------------------------------------------- */

/*
 * A row of filters, one of which is current.
 *
 * **This replaces a `display: flex` typed inline around `.tag` buttons**, which
 * was wrong twice over. The layout was written at the call site, so the one
 * control in this product that filters a list had no definition anywhere and no
 * second surface could reuse it. And `.tag` is a *state* — the note beside it
 * says so: a rule around a word, no fill, the accent kept for the one state
 * that is waiting on the reader. Pressing a state is not a thing a reader can
 * do, so a row of them dressed as buttons taught the opposite of what every
 * other tag on the product means, and `tagAction` marking the current filter
 * spent the accent on navigation rather than on the work.
 *
 * What this is instead is the treatment the rail already uses for exactly this
 * question — see `.workNavLink` under 60rem, which is a horizontal strip of
 * labels with the current one underlined in the accent. Same voice as every
 * other label in this system: mono, uppercase, tracked, muted until it is
 * either hovered or current.
 *
 * The underline is always drawn and is transparent until it is wanted, so
 * nothing shifts between states. A border appearing on hover moves the label,
 * which is the fidgeting every tab strip built the other way does.
 *
 * A scroller rather than a wrap, and the rule under the whole row rather than
 * under each label: seven filters on a narrow window should cost one line and
 * still look like one control.
 */
.filters {
  display: flex;
  gap: var(--gb-space-1);
  overflow-x: auto;
  border-bottom: var(--gb-rule-width) solid var(--gb-rule);
}

.filter {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-muted);
  background: none;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: var(--gb-space-2) var(--gb-space-3);
  margin-bottom: calc(-1 * var(--gb-rule-width));
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: baseline;
  gap: var(--gb-space-2);
  transition:
    color var(--gb-motion-fast) var(--gb-ease),
    border-color var(--gb-motion-fast) var(--gb-ease);
}

.filter:hover {
  color: var(--gb-text);
  border-bottom-color: var(--gb-rule);
}

.filter:focus-visible {
  outline: var(--gb-focus-width) solid var(--gb-accent);
  outline-offset: var(--gb-focus-offset);
}

/*
 * The one that is current, and it is the only thing in the row with the accent
 * on it. Section 07: one accent per view, given to what the reader is doing.
 */
.filterCurrent,
.filterCurrent:hover {
  color: var(--gb-text);
  font-weight: 700;
  border-bottom-color: var(--gb-accent);
}

/*
 * How many are behind the label.
 *
 * Tabular, because these sit in a column of each other as the eye runs along
 * the row and a proportional zero next to a proportional one reads as noise.
 * Faint rather than muted so the word stays the thing being read; the current
 * filter's own colour lifts its count with it.
 */
.filterCount {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  color: var(--gb-text-faint);
}

.filterCurrent .filterCount {
  color: var(--gb-text-muted);
}

/*
 * Moving through a queue one item at a time.
 *
 * The counter sits between the two controls rather than beside them, because
 * *3 of 17* is the thing the two buttons are about and putting it at either end
 * makes the pair read as unrelated.
 */
.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
}

.pagerPosition {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
  margin: 0;
}

/*
 * A labelled block of facts.
 *
 * The queue said everything in `·`-separated lines of `.cardMeta`, which works
 * for three facts and collapses at fifteen: a reader hunting for the price ends
 * up parsing a sentence made of values with no names on them. So a definition
 * list, with the label in the same mono voice `.fieldLabel` and `.statLabel`
 * use — the one this product gives to anything that names a value rather than
 * being one.
 *
 * Two columns where there is room and one where there is not, sized off the
 * label rather than a fraction: these labels are short and a 50/50 split would
 * leave a hand's width of nothing between a word and its value.
 */
.details {
  display: grid;
  grid-template-columns: minmax(9rem, max-content) 1fr;
  gap: var(--gb-space-2) var(--gb-space-4);
  margin: 0;
}

@media (max-width: 40rem) {
  .details {
    grid-template-columns: 1fr;
    gap: var(--gb-space-1) 0;
  }
}

.detailLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
  /* Optical: the label sits on the value's first line rather than above it. */
  padding-top: 0.15em;
}

.detailValue {
  margin: 0;
  font-size: var(--gb-body);
  color: var(--gb-text);
  /*
   * A pitch, a registered address and a provider's refusal all land in here and
   * all three arrive as somebody else's line breaks. `pre-wrap` keeps them and
   * `overflow-wrap` stops a pasted URL pushing the grid past the window.
   */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/*
 * One of several things under a single label.
 *
 * Three proved channels, four signatories: a list inside a value, where a
 * `<ul>` would put a bullet and an indent under a label that is already doing
 * the naming. Each entry is its own line and nothing else changes.
 */
.detailLine {
  display: block;
}

/*
 * A group of facts, named.
 *
 * Three of these stack on a reviewed item — what it is, who it belongs to, who
 * sent it — and without a heading each they read as one very long list. A rule
 * above rather than a box around: section 02's furniture is rules, and a card
 * inside a card is the shape this screen was already too close to.
 */
.detailsGroup {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
  padding-top: var(--gb-space-4);
  border-top: var(--gb-rule-width) solid var(--gb-rule);
}

.detailsTitle {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
  margin: 0;
}

/*
 * The reviewed item itself: one object on the page, given room.
 *
 * `.card` is the list treatment — a tight box meant to sit in a grid of its
 * siblings — and there are no siblings any more. This is the panel's ground
 * with the page's own spacing inside it, so the thing being decided about reads
 * as the page rather than as one row of something longer.
 */
.reviewed {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  background: var(--gb-surface-raised);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: var(--gb-space-6);
}

@media (prefers-reduced-motion: reduce) {
  .filter {
    transition: none;
  }
}
